import { useContext, useEffect, useMemo } from "react";
import { useFlexLayout } from "react-table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  A,
  DashPlaceholder,
  Flex,
  Image,
  PaginationContext,
  SearchContext,
  SortContext,
  SortHeader,
  Text,
} from "dash-ui";
import { useTheme } from "styled-components";
import { FileModel } from "dash-common";

import DeliveryImage from "assets/images/png/Delivery.png";
import { useGetExternalSaleTeamsForDeveloperQuery } from "gql/generated.graphql";
import { RoutesPath } from "flow-components/routes/routes";

import { StyledTable } from "./ExternalSalesPageTable.styles";

const ExternalSalesPageTable = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "tables"]);
  const { t: tLabels } = useTranslation("common", { keyPrefix: "labels" });
  const { state, setTotal, resetPagination, getRefetchProps } =
    useContext(PaginationContext);
  const { state: sortState } = useContext(SortContext);
  const { searchSlug } = useContext(SearchContext);
  const pagination = getRefetchProps(state);

  const { data, loading } = useGetExternalSaleTeamsForDeveloperQuery({
    variables: {
      pagination,
      sort: [sortState],
      filter: {
        ...(searchSlug && { searchText: searchSlug }),
      },
    },
  });

  const { saleTeams, totalCount } = useMemo(
    () => ({
      saleTeams: data?.getExternalSaleTeamsForDeveloper?.collection || [],
      totalCount: data?.getExternalSaleTeamsForDeveloper?.totalCount || 0,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        Header: () => (
          <SortHeader accessor="name" isDefault>
            {tLabels`name`}
          </SortHeader>
        ),
        accessor: "name",
        Cell: ({
          value,
          row,
        }: {
          value: string;
          row: { original: { logoFile?: FileModel; _id: string } };
        }) => (
          <Flex
            alignItems="center"
            onClick={() =>
              navigate(
                RoutesPath.ExternalSalesUsers.replace(":id", row.original._id)
              )
            }
          >
            {row?.original?.logoFile ? (
              <Image mr={2} src={row.original.logoFile.publicUrl} />
            ) : (
              <DashPlaceholder mr={2} />
            )}
            {value}
          </Flex>
        ),
      },
      {
        Header: () => (
          <SortHeader accessor="website">{tLabels`website`}</SortHeader>
        ),
        accessor: "website",
        Cell: ({ value }: { value: string }) => (
          <Flex alignItems="center" h="100%">
            {value ? (
              <A href={value}>{value}</A>
            ) : (
              <Text>{tLabels("emptyColumn")}</Text>
            )}
          </Flex>
        ),
      },
      {
        Header: () => (
          <Text color={theme.colors.n80}>{tLabels`organization`}</Text>
        ),
        accessor: "relatedToSaleTeamOrganizationsNames",
        Cell: ({ value = [] }: { value: string[] }) => (
          <Flex alignItems="center" h="100%">
            <Text color={theme.colors.n80}>
              {value.length ? value.join(", ") : tLabels("emptyColumn")}
            </Text>
          </Flex>
        ),
      },
      {
        Header: () => <Text color={theme.colors.n80}>{tLabels`users`}</Text>,
        width: 50,
        accessor: "countOfUsers",
        Cell: ({ value }: { value: number }) => (
          <Flex alignItems="center" h="100%">
            <Text color={theme.colors.n80}>{value}</Text>
          </Flex>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, tLabels]
  );

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    resetPagination();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchSlug]);

  return (
    <>
      <StyledTable
        tableProps={{
          data: saleTeams,
          columns,
          loading,
          layouts: [useFlexLayout],
          placeholderProps: {
            image: loading ? undefined : DeliveryImage,
            label: loading ? "" : t("tables:externalSales.placeholder") || "",
          },
        }}
        paginationProps={{
          label: t("tables:externalSales.paginationLabel"),
          nextButtonLabel: t("common:labels.next"),
          previousButtonLabel: t("common:labels.previous"),
        }}
      />
    </>
  );
};

export default ExternalSalesPageTable;
